import { allowed } from "./consent";
import { CONSENT_ADVERTISING, CONSENT_ANALYTICS } from "./events";

/**
 * Atribuição de campanha (UTMs + click IDs) com regra last non-direct.
 *
 * - Persistência só em localStorage (escopo de origem/host = isolamento por portal).
 * - Visita direta (sem parâmetros) não sobrescreve a última origem salva.
 * - Guarda os últimos MAX_TOUCHES toques para evolução futura a multi-touch.
 */
export const STORAGE_KEY = "sel_attr";
export const TTL_MS = 30 * 24 * 60 * 60 * 1000;
export const MAX_TOUCHES = 5;

export const CAMPAIGN_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
export const CLICK_ID_PARAMS = ["gclid", "gbraid", "wbraid", "fbclid", "ttclid"];

const MAX_LENGTH = {
    utm_source: 150,
    utm_medium: 150,
    fbclid: 500,
    landing_path: 500,
    default: 255,
};

/**
 * PENDENTE (D8 - jurídico/LGPD): enquanto não houver decisão, a atribuição só é persistida e enviada ao backend
 * com consentimento de analytics ou advertising. Sem consentimento, o toque fica apenas em memória (pending).
 * Se o jurídico classificar como cookie necessário, basta trocar para false.
 */
export const REQUIRE_CONSENT = true;

let pending = null;

function clean(key, value) {
    if (value === null || value === undefined) {
        return null;
    }

    const max = MAX_LENGTH[key] || MAX_LENGTH.default;
    const text = String(value).replace(/[\u0000-\u001f\u007f<>"'`]/g, "").trim().slice(0, max);

    return text === "" ? null : text;
}

function safeStorage() {
    try {
        return window.localStorage;
    } catch (e) {
        return null;
    }
}

function hasConsent() {
    return !REQUIRE_CONSENT || allowed(CONSENT_ANALYTICS) || allowed(CONSENT_ADVERTISING);
}

export function read(storage = safeStorage(), now = Date.now()) {
    try {
        const raw = storage && storage.getItem(STORAGE_KEY);
        if (!raw) {
            return null;
        }

        const state = JSON.parse(raw);
        if (!state || !state.last || !state.expires_at || state.expires_at < now) {
            storage.removeItem(STORAGE_KEY);
            return null;
        }

        return state;
    } catch (e) {
        return null;
    }
}

function write(storage, state) {
    try {
        if (storage) {
            storage.setItem(STORAGE_KEY, JSON.stringify(state));
        }
    } catch (e) {
        // modo privado / storage cheio: atribuição é secundária
    }
}

/**
 * Extrai um toque de campanha da URL. Retorna null para visita direta.
 */
export function extractTouch(search, pathname, referrer, currentHost, now = Date.now()) {
    let params;
    try {
        params = new URLSearchParams(search || "");
    } catch (e) {
        return null;
    }

    const touch = {};
    [...CAMPAIGN_PARAMS, ...CLICK_ID_PARAMS].forEach((key) => {
        const value = clean(key, params.get(key));
        if (value) {
            touch[key] = value;
        }
    });

    if (Object.keys(touch).length === 0) {
        return null;
    }

    touch.landing_path = clean("landing_path", pathname);

    try {
        const referrerHost = referrer ? new URL(referrer).host : null;
        touch.referrer_host = referrerHost && referrerHost !== currentHost ? clean("referrer_host", referrerHost) : null;
    } catch (e) {
        touch.referrer_host = null;
    }

    touch.captured_at = new Date(now).toISOString();

    return touch;
}

/**
 * Last non-direct: um novo toque de campanha vira o "last"; o histórico é limitado a MAX_TOUCHES.
 */
function sameTouch(a, b) {
    return !!a && !!b && [...CAMPAIGN_PARAMS, ...CLICK_ID_PARAMS].every((key) => (a[key] || null) === (b[key] || null));
}

export function merge(state, touch, now = Date.now()) {
    // Mesma campanha capturada de novo (ex.: install + afterEach na mesma URL): só renova a validade
    if (state && sameTouch(state.last, touch)) {
        return { ...state, expires_at: now + TTL_MS };
    }

    const touches = ((state && state.touches) || []).concat([touch]).slice(-MAX_TOUCHES);

    return {
        last: touch,
        touches,
        expires_at: now + TTL_MS,
    };
}

export function capture({
    location = window.location,
    referrer = document.referrer,
    storage = safeStorage(),
    canPersist = hasConsent(),
    now = Date.now(),
} = {}) {
    try {
        const touch = extractTouch(location.search, location.pathname, referrer, location.host, now);
        if (!touch) {
            return;
        }

        if (!canPersist) {
            pending = touch;
            return;
        }

        write(storage, merge(read(storage, now), touch, now));
        pending = null;
    } catch (e) {
        // nunca interrompe a navegação
    }
}

/**
 * Chamado quando o consentimento muda: grava o toque capturado antes do aceite.
 */
export function flushPending({ storage = safeStorage(), canPersist = hasConsent(), now = Date.now() } = {}) {
    if (!pending || !canPersist) {
        return;
    }

    write(storage, merge(read(storage, now), pending, now));
    pending = null;
}

/**
 * Atribuição vigente ({ last, touches }) ou null.
 */
export function get({ storage = safeStorage(), canUse = hasConsent(), now = Date.now() } = {}) {
    if (!canUse) {
        return null;
    }

    const state = read(storage, now);

    return state ? { last: state.last, touches: state.touches || [] } : null;
}

export function resetPendingForTests() {
    pending = null;
}
